import React, { useEffect } from 'react';
import './blog.css';
import { Figure } from './dg';
import ArrowUpRight from '../components/ArrowUpRight';
import {
    DiagramGap,
    DiagramFleet,
    DiagramMath,
    DiagramLoop,
    DiagramReceipts,
    DiagramMatrix,
} from './dg3';

export default function MehngaiBlog() {
    useEffect(() => {
        const prev = document.title;
        document.title = 'India’s inflation rate is a scraper problem — Hritik Kumar';
        return () => {
            document.title = prev;
        };
    }, []);

    return (
        <div className="blog">
            <p className="back">
                <a href="#/">← Hritik Kumar</a>
            </p>

            <header className="blog-head">
                <p className="kicker">Writing · August 2026 · 12 min read</p>
                <h1>India’s inflation rate is a scraper problem</h1>
                <p className="lead">
                    Mehngai computes grocery inflation from four real supermarket
                    chains, and treats the scrapers as the product, not the
                    plumbing.
                </p>
                <p className="blog-links">
                    <a
                        href="https://github.com/devnamrits/mehngai"
                        target="_blank"
                        rel="noreferrer"
                    >
                        github <ArrowUpRight />
                    </a>{' '}
                    ·{' '}
                    <a
                        href="https://mehngai-ui-hr.onrender.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        live index <ArrowUpRight />
                    </a>
                </p>
            </header>

            <article>
                <h2>The number arrives after the damage</h2>
                <p>
                    Official inflation is a monthly instrument. The basket is
                    fixed years in advance, prices are collected by survey, and
                    the release lands weeks after the month it describes. None of
                    that is incompetence. It is what a national statistic has to
                    be: slow, comparable, and hard to argue with.
                </p>
                <p>
                    A household budget does not run on that cadence. Milk, atta,
                    oil and tomatoes reprice when the store feels like it. Between
                    14 and 23 August 2026, the gold series we track moved{' '}
                    <strong>5.28%</strong>. No official release covered that
                    window, because the next one had not been published yet.
                </p>
                <p>
                    The reason nobody publishes a weekly shelf index is not that
                    measuring is hard. It is that <em>staying measured</em> is
                    hard. A scraper that works in July is a scraper that returns
                    zero rows in August, and the index built on it quietly becomes
                    fiction. Any live price index is, underneath, a maintenance
                    problem wearing a statistics costume.
                </p>

                <Figure caption="A monthly survey smooths the year into a line. The shelf actually looks like the right-hand panel: repriced constantly, in small jumps that never get revised.">
                    <DiagramGap />
                </Figure>

                <h2>What Mehngai actually publishes</h2>
                <p>
                    Mehngai reads four Indian grocery chains — Nature’s Basket,
                    Spencer’s, Modern Bazaar and DMart Ready — plus commodity
                    pages for gold and fuel. It normalizes every listing
                    into a comparable unit price, tracks the same items across
                    scans, and publishes a chained index with the first scan as
                    the base of 100.
                </p>
                <p>
                    Around that number sit four smaller surfaces: a meter and
                    ticker for movements between scans, a basket matrix that
                    prices one family’s list at every store, a radar that finds
                    the same product at two stores and ranks the gap, and a live
                    feed of the watchdog’s own decisions. The basket comparison is
                    the useful add-on. The index is the product.
                </p>
                <p>
                    That ordering matters. A price comparator tells you where to
                    shop today. An index tells you whether the thing you buy every
                    week is quietly becoming a larger share of your income. Only
                    one of those is inflation.
                </p>

                <h2>Four chains, picked for a boring reason</h2>
                <p>
                    The target list was constrained before it was interesting. The
                    hackathon’s rules ruled out government sites, anything behind
                    a login or paywall, and any source already covered by Bright
                    Data’s pre-built scraper library. That last rule is the one
                    that shaped the project: if we were going to claim self-healing,
                    we had to own the collectors being healed.
                </p>
                <p>
                    So the four chains are regional, public, catalog-style sites
                    with genuinely different plumbing. Nature’s Basket returns
                    nested JSON. Modern Bazaar needs several category URLs per run.
                    Spencer’s exposes pack sizes in inconsistent places. DMart
                    Ready renders its grid with client-side JavaScript. Four
                    different failure modes is not a bug in the selection. It is
                    the test suite for the watchdog.
                </p>

                <Figure caption="One collector per chain, one output contract, one place where drift is judged. The Collector IDs never change, which is the whole trick.">
                    <DiagramFleet />
                </Figure>

                <p>
                    Each collector was created with Bright Data’s CLI from a
                    category URL and a plain description of the fields we wanted:
                    title, price, pack size, brand, URL. Scraper Studio generated
                    the schema and the scraper, and returned a{' '}
                    <code>c_*</code> Collector ID. From that point on the collector
                    is a production API, not a script on a laptop: we trigger it,
                    poll the dataset, and ingest whatever comes back. Proxies,
                    retries and unblocking are Bright Data’s problem. Knowing
                    whether the rows are still true is ours.
                </p>

                <h2>The math is deliberately boring</h2>
                <p>
                    Every price is converted to a unit price first: ₹/kg for
                    solids, ₹/L for liquids. Amul at ₹33 for 500 ml and ₹66 for
                    1 L are the same price, and the index has no business thinking
                    otherwise. Pack-size parsing is where half the silent errors
                    live, which is why a missing <code>pack_size</code> is treated
                    as a data incident rather than a nullable field.
                </p>
                <p>
                    For each item, each chain, each scan, we take the{' '}
                    <strong>median</strong> across listings rather than the mean.
                    A single mis-scraped ₹9,900 “milk” cannot move a median the way
                    it moves an average. Then the day’s index is yesterday’s index
                    multiplied by the ratio of today’s basket to the day-one
                    basket, with day-one prices frozen as weights. The method
                    string stored with every point is{' '}
                    <code>chained-laspeyres</code>, which is the least glamorous
                    possible answer and the correct one.
                </p>

                <Figure caption="Pack price to unit price to median to ratio to index. Every step is arithmetic; none of it is a model.">
                    <DiagramMath />
                </Figure>

                <p>
                    Scans run <strong>weekly</strong>, not daily. Retail prices
                    oscillate with promotions, and a daily index would mostly
                    measure discount cycles. Weekly medians keep the number on
                    true shelf prices and cut the credit spend by seven. The index
                    still compounds: each new scan multiplies into the previous
                    value, so a year of scans produces a year-long series.
                </p>
                <p>
                    The headline is a median across category clusters, each with
                    its own honest lifecycle. A cluster reads{' '}
                    <em>baseline today</em> until it has a second scan to compare
                    against, then flips to <em>live</em>. Nothing is invented to
                    fill the gap.
                </p>

                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Cluster</th>
                                <th>State at submission</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Groceries</td>
                                <td>
                                    live · four storefronts · 285 products, 584
                                    shelf readings
                                </td>
                            </tr>
                            <tr>
                                <td>Gold</td>
                                <td>
                                    live · nine-day window · +5.28% for 22k and
                                    24k Chennai
                                </td>
                            </tr>
                            <tr>
                                <td>Fuel &amp; commute</td>
                                <td>
                                    baseline · petrol, diesel and LPG collected,
                                    waiting on a second scan
                                </td>
                            </tr>
                            <tr>
                                <td>Electronics &amp; big-ticket</td>
                                <td>connector designed · Apple, Croma</td>
                            </tr>
                            <tr>
                                <td>Travel &amp; airfare</td>
                                <td>connector designed · weekly true-fare scans</td>
                            </tr>
                            <tr>
                                <td>Rent &amp; school fees</td>
                                <td>connector designed · listing and fee pages</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h2>The scraping is not boring</h2>
                <p>
                    Every collection is inspected before it reaches the index.
                    Four checks, each cheap and blunt:
                </p>
                <ul>
                    <li>
                        <strong>empty_run</strong> — zero rows came back. Critical
                        by definition.
                    </li>
                    <li>
                        <strong>null_ratio</strong> — a required field is missing
                        on more than 40% of rows, or on every row, which is
                        reported as <code>schema_drift</code>.
                    </li>
                    <li>
                        <strong>price_outlier</strong> — at least 10% of prices
                        deviate by more than 10× from the run’s median, usually a
                        unit or currency parse error.
                    </li>
                    <li>
                        <strong>contract drift</strong> — the fields we asked for
                        are no longer the fields coming back.
                    </li>
                </ul>
                <p>
                    A critical verdict opens an incident and dispatches a repair
                    with a field-level plain-language prompt. The collector is
                    re-run, re-validated, and only then ingested. The whole loop is
                    receipted: reason, prompt, timestamps, outcome. The product
                    exposes those receipts, which is the only reason to believe any
                    of this.
                </p>

                <Figure caption="Validate, then either store or heal. The re-run is not optional: a heal that has not been verified is just a hope with an API call.">
                    <DiagramLoop />
                </Figure>

                <p>
                    Two operational details cost real hours and are worth writing
                    down. First, an approved heal is not a deployed heal: Scraper
                    Studio stages the repaired template as a draft until{' '}
                    <code>auto_save</code> pushes it to production. We spent one
                    debug cycle watching a “done” heal change nothing. Second, the
                    first heal of any collector is supervised, and only after one
                    verified success does it graduate to autonomous repair. That
                    policy exists because an unsupervised heal can “fix” a schema
                    into something confidently wrong.
                </p>

                <h2>Three receipts</h2>
                <p>
                    These are the three incidents in the submission database, with
                    the prompts the orchestrator actually sent. Each was detected
                    and resolved inside fourteen minutes.
                </p>

                <Figure caption="Three stores, three different failure modes, one loop. The timestamps are UTC; the prompts are verbatim.">
                    <DiagramReceipts />
                </Figure>

                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Chain</th>
                                <th>What the watchdog saw</th>
                                <th>Outcome</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Nature’s Basket</td>
                                <td>
                                    <code>schema_drift</code> — every product
                                    nested inside a single row
                                </td>
                                <td>healed + verified · 14 min</td>
                            </tr>
                            <tr>
                                <td>DMart Ready</td>
                                <td>
                                    <code>empty_run</code> — 46 product pages,
                                    zero listings, because the grid is rendered
                                    client-side
                                </td>
                                <td>healed + verified · 14 min</td>
                            </tr>
                            <tr>
                                <td>Spencer’s</td>
                                <td>
                                    <code>null_ratio</code> — pack size missing
                                    on most rows, which silently breaks every unit
                                    price
                                </td>
                                <td>healed + verified · 14 min</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p>
                    The DMart case is the one worth telling. Re-running returned
                    nothing. A generic heal returned nothing. The actual fix was
                    not a selector: it was a different execution model. The prompt
                    asked for a headless browser, a wait on the product grid, and a
                    scroll to trigger lazy loading. The next run returned priced
                    products, under the same Collector ID. Nothing downstream knew
                    anything had happened, which is exactly the point.
                </p>

                <h2>The Bright Data surface, itemized</h2>
                <p>
                    “We used Scraper Studio” deserves specifics, so here is every
                    part of the platform the product leans on:
                </p>
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Surface</th>
                                <th>What it does here</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <code>scraper create</code>
                                </td>
                                <td>
                                    Builds a collector from a category URL and a
                                    field description. Returns the{' '}
                                    <code>c_*</code> ID that the whole system is
                                    keyed on.
                                </td>
                            </tr>
                            <tr>
                                <td>Discovery collectors</td>
                                <td>
                                    Run against listing pages rather than single
                                    product pages, so one trigger returns a whole
                                    catalog instead of one row.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>POST /dca/trigger</code>
                                </td>
                                <td>
                                    The weekly run. Accepts one or many URLs and
                                    returns a collection ID.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>GET /dca/dataset</code>
                                </td>
                                <td>
                                    Polled until ready, then parsed as JSON rows.
                                    Retries and unblocking happen behind it.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>refactor_template</code>
                                </td>
                                <td>
                                    The repair call. A plain-language prompt
                                    describes the drift; Scraper Studio rewrites
                                    the extraction.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <code>progress</code> +{' '}
                                    <code>resume_automation_job</code>
                                </td>
                                <td>
                                    The approval gate. Progress reports{' '}
                                    <code>awaiting_approval</code> or{' '}
                                    <code>done</code>; resume pushes the repaired
                                    template live.
                                </td>
                            </tr>
                            <tr>
                                <td>stable collector identity</td>
                                <td>
                                    The ID survives every heal, so the trigger,
                                    the contract, the validators and the index
                                    never need reconfiguring.
                                </td>
                            </tr>
                            <tr>
                                <td>managed proxies</td>
                                <td>
                                    Regional storefronts block aggressively. We
                                    never built a proxy pool, and never want to.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h2>What the dashboard shows</h2>
                <p>
                    The index is the headline, but a single number is hard to
                    trust and harder to act on. So the dashboard carries the
                    evidence underneath it. The matrix prices a household’s list
                    at every store, marks the cheapest cell per item and totals it
                    per store, including the stores that do not carry an item. The
                    verdict is three-state rather than binary: cheaper here,
                    cheaper there, or “depends on what you substitute”.
                </p>

                <Figure caption="A real basket, priced across three stores. The cheapest column per row is highlighted; the store total is what actually decides the trip.">
                    <DiagramMatrix />
                </Figure>

                <p>
                    The radar looks for the same product at two stores and ranks
                    the gaps. The ticker streams per-item movements between scans.
                    The pulse feed is the watchdog’s diary, humanized: which chain
                    drifted, what it looked like, whether a heal was dispatched,
                    whether it verified. Most projects hide scraper operations.
                    Mehngai puts them on the page, because “it never goes down” is
                    a claim and a receipt is evidence.
                </p>

                <h2>Honesty is a feature</h2>
                <p>
                    The temptation in a hackathon is to make every number look
                    finished. We took the other route, mostly because the
                    alternative does not survive contact with a judge who asks a
                    second question.
                </p>
                <ul>
                    <li>
                        <strong>Gold is context, never the headline.</strong> The
                        only multi-day series available at submission was gold,
                        and it read +5.28%. A grocery index that quietly leads with
                        gold would be committing exactly the sin it criticizes.
                        Gold sits beside consumer essentials as a savings
                        benchmark.
                    </li>
                    <li>
                        <strong>A cluster with one scan says so.</strong> Groceries
                        had a single full collection day. The index therefore
                        reports a base of 100 and a status, not a fabricated
                        month-over-month figure.
                    </li>
                    <li>
                        <strong>Medians, not means.</strong> One bad parse should
                        not become a national statistic.
                    </li>
                    <li>
                        <strong>Coverage is shown, not averaged away.</strong> One
                        chain contributes 145 items, another 34. The matrix exposes
                        per-store coverage instead of pretending the basket is
                        equally deep everywhere.
                    </li>
                </ul>

                <div className="stats">
                    <div className="stat">
                        <b>285</b>
                        <span>products resolved across four storefronts</span>
                    </div>
                    <div className="stat">
                        <b>584</b>
                        <span>shelf readings in the shipped snapshot</span>
                    </div>
                    <div className="stat">
                        <b>14 min</b>
                        <span>
                            from drift detection to a verified repair, three times
                        </span>
                    </div>
                </div>

                <h2>Limitations, stated plainly</h2>
                <ul>
                    <li>
                        <strong>Weekly resolution.</strong> A mid-week jump is
                        invisible until the next scan. That is a deliberate
                        trade against discount noise, not an oversight.
                    </li>
                    <li>
                        <strong>Fixed weights.</strong> A chained Laspeyres index
                        ignores substitution: if chicken gets expensive and people
                        switch to eggs, the basket does not notice. Official
                        statistics have the same problem and much larger budgets.
                    </li>
                    <li>
                        <strong>Uneven coverage.</strong> Four metro-skewed chains
                        are not India. The index is honest about which shelves it
                        read and silent about the rest.
                    </li>
                    <li>
                        <strong>Healing has a floor.</strong> A repair can fix a
                        schema, a selector or an execution model. It cannot invent
                        a field the site no longer publishes.
                    </li>
                    <li>
                        <strong>DMart is still flaky.</strong> The JS-heavy
                        collector occasionally returns zero rows from a cold host.
                        The watchdog catches it and the next run usually succeeds.
                        We report that instead of hiding it behind a retry.
                    </li>
                    <li>
                        <strong>The grocery series needs time.</strong> A chained
                        index becomes interesting after months, not days. The
                        infrastructure is the deliverable; the history accrues.
                    </li>
                </ul>

                <h2>Run it</h2>
                <p>
                    The repository runs end to end without a Bright Data account,
                    against seeded mock chains. That is the fastest way to see the
                    watchdog work:
                </p>
                <pre>
                    <code>{`git clone https://github.com/devnamrits/mehngai
cd mehngai/backend
python -m venv .venv && source .venv/bin/activate
pip install -r requirements.txt
cp .env.example .env          # mock chains by default
python -m scripts.seed_mock --days 14
uvicorn app.main:app --reload`}</code>
                </pre>
                <p>
                    For the real fleet, pin the collector IDs and trigger a run:
                </p>
                <pre>
                    <code>{`export BRIGHTDATA_API_KEY=...
export COLLECTOR_IDS=c_aaa,c_bbb,c_ccc
curl -X POST -H "Authorization: Bearer $PIPELINE_TOKEN" \\
     localhost:8000/api/v1/pipeline/run`}</code>
                </pre>
                <p>
                    The hosted demo runs at{' '}
                    <a
                        href="https://mehngai-ui-hr.onrender.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        mehngai-ui-hr.onrender.com
                    </a>{' '}
                    on a free tier, so the first visit may take about forty seconds
                    to wake. The API is at{' '}
                    <a
                        href="https://mehngai-api-hr.onrender.com/api/v1/inflation"
                        target="_blank"
                        rel="noreferrer"
                    >
                        mehngai-api-hr.onrender.com
                    </a>
                    .
                </p>
                <p>
                    Everything else is in the repository: the HLD, the product
                    explainer, eleven tests covering validators, normalization,
                    index math and the drift-to-heal path, and the sample API
                    payloads the dashboard actually renders. Inflation is a
                    measurement problem until the day the store changes its HTML.
                    After that, it is a maintenance problem, and the maintenance is
                    the product.
                </p>
            </article>

            <footer className="blog-foot">
                <p>
                    Mehngai was built for the WeMakeDevs × Bright Data{' '}
                    <em>Scrape-Verse</em> hackathon (August 2026), where it placed
                    in the top 50 of 648 submissions. Source:{' '}
                    <a
                        href="https://github.com/devnamrits/mehngai"
                        target="_blank"
                        rel="noreferrer"
                    >
                        github.com/devnamrits/mehngai
                    </a>
                    .
                </p>
            </footer>
        </div>
    );
}
